import { cn } from '@/shared/lib/utils/classnames';
import type { TextBlock as TextBlockType } from '@/shared/types/blocks';

interface TextBlockProps extends Omit<TextBlockType, 'type' | 'id'> {}

/**
 * TextBlock - 본문 텍스트
 *
 * Figma 디자인 기준 (Typography 토큰 사용):
 * - default: paragraph-large (18px, 1.5lh), label-intermediate
 * - muted: paragraph-medium (16px, 1.5lh), label-assistive
 * - emphasis: paragraph-large (18px, 1.5lh), black
 * - small: label-smaller (12px, 1.35lh), label-intermediate
 * - subtitle: label-small-b (14px bold, 1.35lh), black - 서브헤딩용
 * - link: paragraph-medium (16px, 1.5lh), primary (#39f)
 * - body: paragraph-medium (16px, 1.5lh), label-alternative (#67696A) - 설명용
 * - label-medium: 16px bold black (타이틀용)
 * - paragraph-large: 18px regular black (본문용)
 */
export function TextBlock({ content, variant = 'default' }: TextBlockProps) {
  const variantStyles: Record<string, string> = {
    default: 'paragraph-large text-label-intermediate',
    muted: 'paragraph-medium text-label-alternative', // Figma: Paragraph/Medium + #67696A
    emphasis: 'paragraph-large text-black',
    small: 'label-smaller text-label-intermediate',
    subtitle: 'label-small-b text-black', // Figma: Label/Small B (14px bold) + black
    link: 'paragraph-medium text-primary',
    body: 'paragraph-medium text-label-alternative', // Figma: Paragraph/Medium (16px) + #67696A
    description: 'text-[16px] font-normal leading-[1.35] text-label-neutral', // Figma: 16px, normal, lh 135%, #252627
    // Figma typography tokens (직접 매핑)
    'label-medium': 'text-[16px] font-bold leading-[1.35] text-black',
    'paragraph-large': 'text-[18px] font-normal leading-[1.5] text-black',
    // 핵심 요약 본문 (Figma: Paragraph/Medium, 중앙정렬, 흰색)
    highlight: 'text-[16px] font-normal leading-[1.5] text-white text-center',
  };

  return (
    <p
      className={cn('whitespace-pre-wrap', variantStyles[variant] || variantStyles.default)}
      style={
        variant === 'highlight' ? { fontFeatureSettings: "'liga' off, 'clig' off" } : undefined
      }
    >
      {content}
    </p>
  );
}
